import React, { useEffect, useState } from 'react';
import Bredcrum from '../../common/Bredcrum';
import CoursesHero from './CoursesHero';
import CourseList from './CourseList';
import CourseLocations from './CourseLocations';
import { axiosInstance } from '../config';
import {
  CATEGORY_TABS,
  DURATION_BUCKETS,
  PRICE_BUCKETS,
  SORT_OPTIONS,
  DEFAULT_FILTERS,
} from './courseFilterUtils';
import '../../../assets/styles/coursesHero.css';
import '../../../assets/styles/courseFilters.css';
import '../../../assets/styles/courseCards.css';

const CoursesIndex = () => {
  const [Courses, setCourses] = useState([]);
  const [Loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('all');
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState('recommended');

  useEffect(() => {
    getData();
  }, []);

  // Preserve scroll position across navigation so returning via the browser's
  // back button (e.g. after following a "View All Areas" link to Contact Us)
  // lands back on the same spot instead of snapping to the top.
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem('courses-page-scroll-y', String(window.scrollY));
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (Courses.length === 0) return;
    const returnToLocations = sessionStorage.getItem(
      'courses-return-to-locations'
    );

    if (returnToLocations) {
      sessionStorage.removeItem('courses-return-to-locations');
      requestAnimationFrame(() => {
        const locationsSection = document.getElementById(
          'course-locations-section'
        );
        if (locationsSection) {
          const headerOffset = 86;
          const top =
            locationsSection.getBoundingClientRect().top +
            window.scrollY -
            headerOffset;
          window.scrollTo({ top, behavior: 'auto' });
        }
      });
      return;
    }

    const saved = sessionStorage.getItem('courses-page-scroll-y');
    if (saved) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: Number(saved), behavior: 'auto' });
      });
    }
  }, [Courses.length]);

  function getData() {
    setLoading(true);

    axiosInstance
      .get(`get-cources`)
      .then((res) => {
        setCourses(
          res.data.cources.filter((course) => course.status === 0).reverse()
        );
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setLoading(false);
      });
  }

  function handleTabClick(tabValue) {
    setActiveTab(tabValue);
    setAppliedFilters((prev) => ({ ...prev, courseType: tabValue }));
    setDraftFilters((prev) => ({ ...prev, courseType: tabValue }));
  }

  function handleDraftChange(key, value) {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleApplyFilters() {
    setAppliedFilters(draftFilters);
    setActiveTab(draftFilters.courseType);
  }

  function handleClearFilters() {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setActiveTab('all');
  }

  return (
    <>
      <CoursesHero />
      <Bredcrum
        heading={''}
        path={[{ name: 'Courses', path: 'courses' }]}
        variant="highlighted"
      />

      <div className="course-browse-wrap">
      <div className="course-tabs-sticky-wrap">
        <div className="auto-container">
          <div className="course-tabs" role="tablist">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`course-tab${activeTab === tab.value ? ' active' : ''}`}
                onClick={() => handleTabClick(tab.value)}
              >
                <i className={`bi ${tab.icon}`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="course-filters-section">
        <div className="auto-container">
          <div className="course-filters-card">
            <div className="row g-4 align-items-end">
              <div className="col-lg-3 col-md-6">
                <div className="filter-group">
                  <label className="filter-label">
                    <span className="filter-icon">
                      <i className="bi bi-car-front-fill"></i>
                    </span>
                    Transmission
                  </label>
                  <select
                    className="form-select filter-select"
                    value={draftFilters.transmission}
                    onChange={(e) =>
                      handleDraftChange('transmission', e.target.value)
                    }
                  >
                    <option value="all">All</option>
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="filter-group">
                  <label className="filter-label">
                    <span className="filter-icon">
                      <i className="bi bi-grid-fill"></i>
                    </span>
                    Course Type
                  </label>
                  <select
                    className="form-select filter-select"
                    value={draftFilters.courseType}
                    onChange={(e) =>
                      handleDraftChange('courseType', e.target.value)
                    }
                  >
                    {CATEGORY_TABS.map((tab) => (
                      <option key={tab.value} value={tab.value}>
                        {tab.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="filter-group">
                  <label className="filter-label">
                    <span className="filter-icon">
                      <i className="bi bi-clock-fill"></i>
                    </span>
                    Duration
                  </label>
                  <select
                    className="form-select filter-select"
                    value={draftFilters.duration}
                    onChange={(e) =>
                      handleDraftChange('duration', e.target.value)
                    }
                  >
                    {DURATION_BUCKETS.map((bucket) => (
                      <option key={bucket.value} value={bucket.value}>
                        {bucket.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="filter-group">
                  <label className="filter-label">
                    <span className="filter-icon">
                      <i className="bi bi-currency-pound"></i>
                    </span>
                    Price Range
                  </label>
                  <select
                    className="form-select filter-select"
                    value={draftFilters.price}
                    onChange={(e) => handleDraftChange('price', e.target.value)}
                  >
                    {PRICE_BUCKETS.map((bucket) => (
                      <option key={bucket.value} value={bucket.value}>
                        {bucket.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="course-filters-actions">
              <button
                type="button"
                className="btn-apply-filters"
                onClick={handleApplyFilters}
              >
                <i className="bi bi-funnel-fill me-2"></i>
                Apply Filters
              </button>
              <button
                type="button"
                className="btn-clear-filters"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>

            <div className="course-sort-row">
              <span className="course-sort-label">Sort by:</span>
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`course-sort-link${sortBy === opt.value ? ' active' : ''}`}
                  onClick={() => setSortBy(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CourseList
        Courses={Courses}
        Loading={Loading}
        filters={appliedFilters}
        sortBy={sortBy}
      />
      </div>

      <CourseLocations />
    </>
  );
};

export default CoursesIndex;
